import React from 'react';
import HomeIcon from './HomeIcon';
import MarketingHeader from './MarketingHeader';
import type { ContactLandingVals } from './useContactLanding';
import { getContactCopy } from './contactCopy';
import './HomeLanding.css';

type Props = {
  v: ContactLandingVals;
  onSetCz: () => void;
  onSetEn: () => void;
  onContactSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  onOpenLiveChat: () => void;
  lang: 'cs' | 'en';
  submitting?: boolean;
};

const iconBadge: React.CSSProperties = {
  width: 40,
  height: 40,
  borderRadius: 12,
  background: '#EAF1FF',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 'none',
};

const ContactLanding: React.FC<Props> = ({ v, onSetCz, onSetEn, onContactSubmit, onOpenLiveChat, lang, submitting }) => {
  const c = getContactCopy(lang);
  const [heroBefore, heroAfter] = c.heroTitleBefore.split('\n');

  return (
    <div className="home-landing" style={{ width: '100%', fontFamily: 'Inter, system-ui, sans-serif' }}>
      <MarketingHeader v={v} lang={lang} onSetCz={onSetCz} onSetEn={onSetEn} active="contact" />

      <section
        id="top"
        style={{
          position: 'relative',
          padding: 'clamp(112px,18vw,156px) 20px clamp(48px,8vw,80px)',
          background: 'radial-gradient(1100px 620px at 50% -10%, #EAF1FF 0%, rgba(234,241,255,0) 70%), #FFFFFF',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: 'radial-gradient(#0065F4 1px, transparent 1px)',
            backgroundSize: '28px 28px',
            opacity: 0.07,
            maskImage: 'radial-gradient(700px 500px at 50% 20%, #000 20%, transparent 80%)',
            WebkitMaskImage: 'radial-gradient(700px 500px at 50% 20%, #000 20%, transparent 80%)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: 180,
            background: 'linear-gradient(180deg, rgba(247,248,250,0) 0%, #F7F8FA 100%)',
            pointerEvents: 'none',
          }}
        />
        <div
          data-hero-grid
          style={{
            position: 'relative',
            maxWidth: 1200,
            margin: '0 auto',
            display: 'grid',
            gridTemplateColumns: '1.05fr 0.95fr',
            gap: 56,
            alignItems: 'center',
          }}
        >
          <div data-hero-copy>
            <h1
              style={{
                margin: 0,
                fontSize: 'clamp(32px,6vw,56px)',
                lineHeight: 1.06,
                letterSpacing: '-0.03em',
                fontWeight: 700,
                color: '#111827',
                textWrap: 'balance',
              }}
            >
              {heroBefore}
              <br />
              {heroAfter}
              <span style={{ color: '#0065F4' }}>{c.heroTitleHighlight}</span>
            </h1>
            <p
              style={{
                margin: '20px 0 0',
                maxWidth: 520,
                fontSize: 'clamp(16px,2.2vw,19px)',
                lineHeight: 1.6,
                color: '#374151',
                textWrap: 'pretty',
              }}
            >
              {c.heroSub}
            </p>
            <div data-hero-cta style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 32 }}>
              <a
                href="#formular"
                className="home-btn-primary"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 46,
                  padding: '0 24px',
                  background: '#0065F4',
                  color: '#fff',
                  fontSize: 15,
                  fontWeight: 600,
                  lineHeight: 1,
                  borderRadius: 10,
                  transition: 'all 150ms ease-out',
                }}
              >
                {c.heroCtaWrite}
              </a>
              <button
                type="button"
                onClick={onOpenLiveChat}
                className="home-btn-secondary"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 9,
                  height: 46,
                  padding: '0 20px',
                  background: '#fff',
                  color: '#111827',
                  fontSize: 15,
                  fontWeight: 600,
                  lineHeight: 1,
                  borderRadius: 10,
                  border: '1px solid #E5E7EB',
                  transition: 'all 150ms ease-out',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                <HomeIcon name="chat" size={16} color="#111827" trigger="hover" target="button" />
                {c.heroCtaSchedule}
              </button>
            </div>
          </div>

          <div data-illu style={{ position: 'relative', width: 'min(460px,100%)', height: 400, justifySelf: 'end' }}>
            <img
              src="/home/macbook-smeny.png"
              alt="Ompli - plánování směn"
              style={{
                position: 'absolute',
                inset: '22px 10px 30px 18px',
                width: 'auto',
                height: 'auto',
                maxWidth: '100%',
                maxHeight: '100%',
                margin: 'auto',
                display: 'block',
                objectFit: 'contain',
              }}
            />

            <div
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                width: 230,
                background: '#fff',
                border: '1px solid #EDF0F5',
                borderRadius: 18,
                padding: 16,
                boxShadow: '0 16px 36px rgba(17,24,39,0.08)',
                animation: 'home-floatCard 5.4s ease-in-out infinite',
                rotate: '-3deg',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{c.calendarMonth}</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#0065F4', background: '#EAF1FF', borderRadius: 999, padding: '3px 8px' }}>
                  {c.calendarBadge}
                </span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 5 }}>
                {v.days.map((d) => (
                  <span
                    key={d.n}
                    style={{
                      height: 22,
                      borderRadius: 6,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 10,
                      fontWeight: 600,
                      background: d.bg,
                      color: d.fg,
                    }}
                  >
                    {d.n}
                  </span>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
                <span style={{ flex: 1, textAlign: 'center', fontSize: 11, fontWeight: 600, color: '#0065F4', background: '#EAF1FF', borderRadius: 8, padding: '7px 0' }}>
                  10:00
                </span>
                <span style={{ flex: 1, textAlign: 'center', fontSize: 11, fontWeight: 600, color: '#fff', background: '#0065F4', borderRadius: 8, padding: '7px 0' }}>
                  13:30
                </span>
                <span style={{ flex: 1, textAlign: 'center', fontSize: 11, fontWeight: 600, color: '#6B7280', background: '#F7F8FA', borderRadius: 8, padding: '7px 0' }}>
                  15:00
                </span>
              </div>
            </div>

            <div
              style={{
                position: 'absolute',
                right: 0,
                top: 172,
                width: 214,
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                animation: 'home-floatCard 6.2s ease-in-out infinite',
                animationDelay: '0.9s',
              }}
            >
              <div
                style={{
                  alignSelf: 'flex-start',
                  maxWidth: 190,
                  background: '#fff',
                  border: '1px solid #EDF0F5',
                  borderRadius: '16px 16px 16px 5px',
                  padding: '12px 15px',
                  fontSize: 13,
                  lineHeight: 1.5,
                  color: '#111827',
                  boxShadow: '0 10px 26px rgba(17,24,39,0.07)',
                }}
              >
                {c.chatIn}
              </div>
              <div
                style={{
                  alignSelf: 'flex-end',
                  maxWidth: 200,
                  background: '#0065F4',
                  borderRadius: '16px 16px 5px 16px',
                  padding: '12px 15px',
                  fontSize: 13,
                  lineHeight: 1.5,
                  color: '#fff',
                  boxShadow: '0 10px 26px rgba(0,101,244,0.22)',
                }}
              >
                {c.chatOut}
              </div>
            </div>

            <div
              style={{
                position: 'absolute',
                left: 14,
                bottom: 8,
                background: '#fff',
                border: '1px solid #EDF0F5',
                borderRadius: 16,
                padding: '13px 17px',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                boxShadow: '0 14px 32px rgba(17,24,39,0.08)',
                animation: 'home-floatCard 4.8s ease-in-out infinite',
                animationDelay: '1.6s',
                rotate: '2deg',
              }}
            >
              <div style={{ display: 'flex' }}>
                <span style={{ width: 30, height: 30, borderRadius: 999, background: '#0065F4', border: '2px solid #fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
                  <HomeIcon name="avatar" size={15} color="#fff" trigger="loop-on-hover" />
                </span>
                <span style={{ width: 30, height: 30, borderRadius: 999, background: '#7FA9F7', border: '2px solid #fff', marginLeft: -10, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
                  <HomeIcon name="avatar" size={15} color="#fff" trigger="loop-on-hover" />
                </span>
                <span style={{ width: 30, height: 30, borderRadius: 999, background: '#FFD600', border: '2px solid #fff', marginLeft: -10, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
                  <HomeIcon name="avatar" size={15} color="#111827" trigger="loop-on-hover" />
                </span>
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#6B7280', whiteSpace: 'nowrap' }}>{c.teamOnlineLabel}</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', whiteSpace: 'nowrap' }}>{c.teamOnlineHours}</div>
              </div>
              <span style={{ position: 'relative', width: 10, height: 10, flex: 'none' }}>
                <span style={{ position: 'absolute', inset: 0, borderRadius: 999, background: '#16A34A' }} />
                <span className="home-pulse-ring" style={{ position: 'absolute', inset: 0, borderRadius: 999, background: '#16A34A' }} />
              </span>
            </div>
          </div>
        </div>
      </section>

      <section id="formular" style={{ background: '#fff', borderTop: '1px solid #EDF0F5', padding: 'clamp(56px,9vw,96px) 20px clamp(48px,8vw,88px)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div data-contact-grid style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, alignItems: 'start' }}>
            <form
              onSubmit={onContactSubmit}
              style={{
                background: '#fff',
                border: '1px solid #EDF0F5',
                borderRadius: 20,
                padding: 'clamp(20px,4.4vw,32px)',
                display: 'flex',
                flexDirection: 'column',
                gap: 26,
                boxShadow: '0 12px 32px rgba(17,24,39,0.05)',
              }}
            >
              <div style={{ marginBottom: 12 }}>
                <h2 style={{ margin: 0, fontSize: 'clamp(21px,3vw,26px)', lineHeight: 1.2, letterSpacing: '-0.02em', fontWeight: 700, color: '#111827' }}>
                  {c.formTitle}
                </h2>
                <p style={{ margin: '8px 0 0', fontSize: 15, lineHeight: 1.6, color: '#6B7280' }}>{c.formSub}</p>
              </div>
              <div data-name-grid style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
                <div>
                  <label htmlFor="k-firstName" style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', margin: '0 0 6px' }}>
                    {c.labelFirstName}
                  </label>
                  <input
                    id="k-firstName"
                    name="firstName"
                    type="text"
                    placeholder={c.placeholderFirstName}
                    className="home-field"
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      fontFamily: 'inherit',
                      fontSize: 15,
                      color: '#111827',
                      background: '#F7F8FA',
                      border: '1px solid #EDF0F5',
                      borderRadius: 12,
                      padding: '12px 14px',
                      outline: 'none',
                      transition: 'border-color 160ms ease-out, background 160ms ease-out',
                    }}
                  />
                </div>
                <div>
                  <label htmlFor="k-lastName" style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', margin: '0 0 6px' }}>
                    {c.labelLastName}
                  </label>
                  <input
                    id="k-lastName"
                    name="lastName"
                    type="text"
                    placeholder={c.placeholderLastName}
                    className="home-field"
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      fontFamily: 'inherit',
                      fontSize: 15,
                      color: '#111827',
                      background: '#F7F8FA',
                      border: '1px solid #EDF0F5',
                      borderRadius: 12,
                      padding: '12px 14px',
                      outline: 'none',
                      transition: 'border-color 160ms ease-out, background 160ms ease-out',
                    }}
                  />
                </div>
              </div>
              <div>
                <label htmlFor="k-email" style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', margin: '0 0 6px' }}>
                  {c.labelEmail}
                </label>
                <input
                  id="k-email"
                  name="email"
                  type="email"
                  placeholder={c.placeholderEmail}
                  className="home-field"
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    fontFamily: 'inherit',
                    fontSize: 15,
                    color: '#111827',
                    background: '#F7F8FA',
                    border: '1px solid #EDF0F5',
                    borderRadius: 12,
                    padding: '12px 14px',
                    outline: 'none',
                    transition: 'border-color 160ms ease-out, background 160ms ease-out',
                  }}
                />
              </div>
              <div>
                <label htmlFor="k-company" style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', margin: '0 0 6px' }}>
                  {c.labelCompany}
                </label>
                <input
                  id="k-company"
                  name="company"
                  type="text"
                  placeholder={c.placeholderCompany}
                  className="home-field"
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    fontFamily: 'inherit',
                    fontSize: 15,
                    color: '#111827',
                    background: '#F7F8FA',
                    border: '1px solid #EDF0F5',
                    borderRadius: 12,
                    padding: '12px 14px',
                    outline: 'none',
                    transition: 'border-color 160ms ease-out, background 160ms ease-out',
                  }}
                />
              </div>
              <div style={{ position: 'relative' }}>
                <label htmlFor="k-subject" style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', margin: '0 0 6px' }}>
                  {c.labelSubject}
                </label>
                <select
                  id="k-subject"
                  name="subject"
                  className="home-field"
                  defaultValue=""
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    fontFamily: 'inherit',
                    fontSize: 15,
                    color: '#111827',
                    background: '#F7F8FA',
                    border: '1px solid #EDF0F5',
                    borderRadius: 12,
                    padding: '12px 14px',
                    outline: 'none',
                    transition: 'border-color 160ms ease-out, background 160ms ease-out',
                    appearance: 'none',
                    backgroundImage: 'none',
                  }}
                >
                  <option value="">{c.placeholderSubject}</option>
                  {c.subjects.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                <span style={{ position: 'absolute', right: 14, top: 'calc(50% + 12px)', pointerEvents: 'none', display: 'block' }}>
                  <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                    <path d="M1 1.5L6 6.5L11 1.5" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </span>
              </div>
              <div>
                <label htmlFor="k-message" style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', margin: '0 0 6px' }}>
                  {c.labelMessage}
                </label>
                <textarea
                  id="k-message"
                  name="message"
                  rows={5}
                  placeholder={c.placeholderMessage}
                  className="home-field"
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    fontFamily: 'inherit',
                    fontSize: 15,
                    color: '#111827',
                    background: '#F7F8FA',
                    border: '1px solid #EDF0F5',
                    borderRadius: 12,
                    padding: '12px 14px',
                    outline: 'none',
                    transition: 'border-color 160ms ease-out, background 160ms ease-out',
                    resize: 'vertical',
                    lineHeight: 1.6,
                  }}
                />
              </div>
              <button
                type="submit"
                className="home-submit"
                disabled={submitting}
                style={{
                  marginTop: 4,
                  fontFamily: 'inherit',
                  fontSize: 15,
                  fontWeight: 600,
                  color: '#fff',
                  background: '#0065F4',
                  border: 0,
                  borderRadius: 12,
                  padding: '14px 20px',
                  cursor: submitting ? 'wait' : 'pointer',
                  opacity: submitting ? 0.75 : 1,
                  transition: 'background 160ms ease-out',
                }}
              >
                {c.submit}
              </button>
              <p style={{ margin: 0, fontSize: 13, color: '#6B7280' }}>{c.consent}</p>
            </form>

            <div id="termin" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 'clamp(21px,3vw,26px)', lineHeight: 1.2, letterSpacing: '-0.02em', fontWeight: 700, color: '#111827' }}>
                  {c.scheduleTitle}
                </h2>
                <p style={{ margin: '8px 0 0', fontSize: 15, lineHeight: 1.6, color: '#6B7280' }}>{c.scheduleSub}</p>
              </div>
              <div
                data-contact-frame
                style={{
                  background: '#F7F8FA',
                  border: '1px solid #EDF0F5',
                  borderRadius: 20,
                  padding: 8,
                  overflow: 'hidden',
                  minHeight: 660,
                  display: 'flex',
                }}
              >
                <iframe
                  src="https://calendly.com/ompli/30min?hide_gdpr_banner=1&primary_color=0065f4"
                  title="Rezervace schůzky"
                  style={{ width: '100%', height: '100%', minHeight: 644, border: 0, borderRadius: 14, background: '#fff' }}
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="tym" style={{ background: '#F7F8FA', padding: 'clamp(56px,9vw,96px) 20px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div data-team-grid style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'clamp(28px,5vw,56px)', alignItems: 'start' }}>
            <div>
              <h2 style={{ margin: 0, fontSize: 'clamp(21px,3vw,26px)', lineHeight: 1.2, letterSpacing: '-0.02em', fontWeight: 700, color: '#111827' }}>
                {c.teamTitle}
              </h2>
              <div data-people style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-start', gap: 28, marginTop: 26 }}>
                {c.team.map((p) => (
                  <div
                    key={p.name}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      gap: 12,
                      width: 140,
                      maxWidth: '100%',
                    }}
                  >
                    <div
                      style={{
                        width: 'clamp(78px,13vw,96px)',
                        height: 'clamp(78px,13vw,96px)',
                        borderRadius: 999,
                        overflow: 'hidden',
                        background: 'repeating-linear-gradient(135deg,#EAF1FF 0 8px,#F3F7FF 8px 16px)',
                        border: '1px solid #E3EBFB',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <span
                        style={{
                          fontFamily: "ui-monospace,'SF Mono',Menlo,monospace",
                          fontSize: 10,
                          letterSpacing: '0.03em',
                          color: '#7FA9F7',
                          textTransform: 'uppercase',
                        }}
                      >
                        foto
                      </span>
                    </div>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: '#111827', letterSpacing: '-0.01em', lineHeight: 1.25 }}>{p.name}</div>
                      <div style={{ fontSize: 13, fontWeight: 500, color: '#6B7280', marginTop: 4, lineHeight: 1.35 }}>{p.role}</div>
                    </div>
                  </div>
                ))}
              </div>

              <h3 style={{ margin: '44px 0 0', fontSize: 'clamp(21px,3vw,26px)', lineHeight: 1.2, letterSpacing: '-0.02em', fontWeight: 700, color: '#111827' }}>
                {c.expectTitle}
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', marginTop: 20 }}>
                {c.expect.map((e) => (
                  <div key={e.title} style={{ display: 'flex', alignItems: 'flex-start', gap: 16, padding: '18px 0', borderTop: '1px solid #EDF0F5' }}>
                    <span style={iconBadge}>
                      <HomeIcon name={e.icon} size={19} trigger="loop-on-hover" />
                    </span>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 600, color: '#111827' }}>{e.title}</div>
                      <p style={{ margin: '5px 0 0', fontSize: 15, lineHeight: 1.55, color: '#6B7280', textWrap: 'pretty' }}>{e.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <h2 style={{ margin: 0, fontSize: 'clamp(21px,3vw,26px)', lineHeight: 1.2, letterSpacing: '-0.02em', fontWeight: 700, color: '#111827' }}>
                {c.preferTitle}
              </h2>

              <a
                href={`mailto:${c.emailValue}`}
                className="home-contact-card"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 22,
                  background: '#fff',
                  border: '1px solid #EDF0F5',
                  borderRadius: 16,
                  padding: '18px 20px',
                  marginTop: 16,
                  transition: 'border-color 160ms ease-out, transform 160ms ease-out',
                }}
              >
                <span style={iconBadge}>
                  <HomeIcon name="email" size={18} trigger="hover" target="a" />
                </span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: '#6B7280' }}>{c.emailLabel}</div>
                  <div style={{ fontSize: 17, fontWeight: 600, color: '#111827', letterSpacing: '-0.01em', overflowWrap: 'anywhere' }}>{c.emailValue}</div>
                </div>
              </a>

              <a
                href={`tel:${c.phoneHref}`}
                className="home-contact-card"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 22,
                  background: '#fff',
                  border: '1px solid #EDF0F5',
                  borderRadius: 16,
                  padding: '18px 20px',
                  transition: 'border-color 160ms ease-out, transform 160ms ease-out',
                }}
              >
                <span style={iconBadge}>
                  <HomeIcon name="phone" size={18} trigger="hover" target="a" />
                </span>
                <div>
                  <div style={{ fontSize: 13, color: '#6B7280' }}>{c.phoneLabel}</div>
                  <div style={{ fontSize: 17, fontWeight: 600, color: '#111827', letterSpacing: '-0.01em' }}>{c.phoneValue}</div>
                </div>
              </a>

              <div style={{ background: '#fff', border: '1px solid #EDF0F5', borderRadius: 16, padding: '22px 24px', marginTop: 12 }}>
                <div style={{ fontSize: 13, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6B7280' }}>{c.billingTitle}</div>
                <div style={{ fontSize: 17, fontWeight: 700, color: '#111827', letterSpacing: '-0.01em', marginTop: 12 }}>{c.billingCompany}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginTop: 14 }}>
                  {c.billing.map((b) => (
                    <div key={b.k} style={{ display: 'flex', alignItems: 'baseline', gap: 14, fontSize: 15, lineHeight: 1.5 }}>
                      <span style={{ flex: 'none', width: 74, color: '#6B7280' }}>{b.k}</span>
                      <span style={{ color: '#111827', fontWeight: 500 }}>{b.v}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section style={{ background: '#fff', padding: 'clamp(56px,9vw,96px) 20px' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', maxWidth: 600, margin: '0 auto 40px' }}>
            <h2 style={{ margin: 0, fontSize: 'clamp(24px,4.2vw,34px)', lineHeight: 1.15, letterSpacing: '-0.025em', fontWeight: 700, color: '#111827' }}>
              {c.faqTitle}
            </h2>
          </div>
          <div data-faq-grid style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {c.faq.map((f) => (
              <div key={f.q} style={{ background: '#F7F8FA', border: '1px solid #EDF0F5', borderRadius: 16, padding: '24px 26px' }}>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#111827', lineHeight: 1.35 }}>{f.q}</div>
                <p style={{ margin: '10px 0 0', fontSize: 15, lineHeight: 1.6, color: '#6B7280', textWrap: 'pretty' }}>{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer style={{ background: '#fff', borderTop: '1px solid #EDF0F5', padding: 'clamp(44px,7vw,64px) 20px 32px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div data-footer-grid style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr 1fr 1fr 1fr', gap: 40 }}>
            <div>
              <img src="/home/ompli-logo-blue.png" alt="Ompli" style={{ height: 24, width: 'auto', display: 'block' }} />
              <p style={{ margin: '16px 0 0', fontSize: 15, lineHeight: 1.6, color: '#6B7280', maxWidth: 260, textWrap: 'pretty' }}>{c.footerTagline}</p>
              <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                <a href="#li" aria-label="LinkedIn" className="home-social" style={{ width: 34, height: 34, borderRadius: 10, background: '#F7F8FA', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6B7280', transition: 'all 150ms ease-out' }}>
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5zM2.75 9.25h4.46V21H2.75V9.25zm7.02 0h4.27v1.6h.06c.6-1.07 2.06-1.94 3.79-1.94 3.24 0 4.36 1.94 4.36 5.35V21h-4.46v-5.72c0-1.44-.52-2.42-1.8-2.42-1.05 0-1.67.71-1.94 1.4-.1.24-.13.58-.13.92V21H9.77s.06-10.63 0-11.75z" />
                  </svg>
                </a>
                <a href="#fb" aria-label="Facebook" className="home-social" style={{ width: 34, height: 34, borderRadius: 10, background: '#F7F8FA', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6B7280', transition: 'all 150ms ease-out' }}>
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5.02 3.66 9.18 8.44 9.94v-7.03H7.9v-2.91h2.54V9.85c0-2.52 1.49-3.91 3.77-3.91 1.09 0 2.24.2 2.24.2v2.47h-1.26c-1.24 0-1.63.78-1.63 1.57v1.88h2.78l-.45 2.91h-2.33V22c4.78-.76 8.44-4.92 8.44-9.94z" />
                  </svg>
                </a>
                <a href="#ig" aria-label="Instagram" className="home-social" style={{ width: 34, height: 34, borderRadius: 10, background: '#F7F8FA', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6B7280', transition: 'all 150ms ease-out' }}>
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                    <rect x="3" y="3" width="18" height="18" rx="5" />
                    <circle cx="12" cy="12" r="4" />
                    <circle cx="17.3" cy="6.7" r="1.1" fill="currentColor" stroke="none" />
                  </svg>
                </a>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{c.footerProduct}</div>
              <a href="/#funkce" className="home-footer-link" style={{ fontSize: 15, color: '#6B7280' }}>{c.footerShifts}</a>
              <a href="/#funkce" className="home-footer-link" style={{ fontSize: 15, color: '#6B7280' }}>{c.footerAttendance}</a>
              <a href="/#funkce" className="home-footer-link" style={{ fontSize: 15, color: '#6B7280' }}>{c.footerContracts}</a>
              <a href="/#funkce" className="home-footer-link" style={{ fontSize: 15, color: '#6B7280' }}>{c.footerHiring}</a>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{c.footerCompany}</div>
              <a href="/o-nas" className="home-footer-link" style={{ fontSize: 15, color: '#6B7280' }}>{c.footerAbout}</a>
              <a href="#kariera" className="home-footer-link" style={{ fontSize: 15, color: '#6B7280' }}>{c.footerCareers}</a>
              <a href="/blog" className="home-footer-link" style={{ fontSize: 15, color: '#6B7280' }}>{c.footerBlog}</a>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{c.footerSupport}</div>
              <a href="/docs" className="home-footer-link" style={{ fontSize: 15, color: '#6B7280' }}>{c.footerHelp}</a>
              <a href="#top" className="home-footer-link" style={{ fontSize: 15, color: '#6B7280' }}>{c.footerContact}</a>
              <a href="#status" className="home-footer-link" style={{ fontSize: 15, color: '#6B7280' }}>{c.footerStatus}</a>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{c.footerLegal}</div>
              <a href="#gdpr" className="home-footer-link" style={{ fontSize: 15, color: '#6B7280' }}>{c.footerGdpr}</a>
              <a href="#podminky" className="home-footer-link" style={{ fontSize: 15, color: '#6B7280' }}>{c.footerTerms}</a>
              <a href="#cookies" className="home-footer-link" style={{ fontSize: 15, color: '#6B7280' }}>{c.footerCookies}</a>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap', marginTop: 48, paddingTop: 24, borderTop: '1px solid #EDF0F5' }}>
            <span style={{ fontSize: 14, color: '#6B7280' }}>© 2026 Ompli s.r.o.</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <span style={{ fontSize: 14, color: '#6B7280' }}>{v.langLabel} · CZK</span>
              <a href="#privacy" className="home-footer-link" style={{ fontSize: 14, color: '#6B7280' }}>{c.footerPrivacy}</a>
              <a href="#terms" className="home-footer-link" style={{ fontSize: 14, color: '#6B7280' }}>{c.footerTermsShort}</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ContactLanding;
